import { appLocalStorage } from '../utils/storage';
import { DRAWING_COLORS } from './model';
import { validHex } from './color';
import { isDeletedOwner } from '../accountDeletion/ownerState';

const memory = new Map();
const keyFor = userId => /^[1-9]\d*$/.test(String(userId)) && Number.isSafeInteger(Number(userId))
  ? `drawing-palette:${userId}` : null;
export const defaultPalette = () => ({ version: 1, colors: [...DRAWING_COLORS], recent: [...DRAWING_COLORS] });
const validPalette = value => value?.version === 1
  && Array.isArray(value.colors) && value.colors.length === DRAWING_COLORS.length
  && value.colors.every(color => typeof color === 'string' && validHex(color) && color === color.toUpperCase())
  && new Set(value.colors).size === DRAWING_COLORS.length
  && Array.isArray(value.recent) && value.recent.length === value.colors.length
  && new Set(value.recent).size === value.colors.length && value.recent.every(color => value.colors.includes(color));

export function readPalette(userId) {
  if (isDeletedOwner(userId)) return defaultPalette();
  const key = keyFor(userId);
  if (!key) return defaultPalette();
  if (memory.has(key)) return memory.get(key);
  try {
    const raw = appLocalStorage.getItem(key);
    const value = raw && raw.length <= 1024 ? JSON.parse(raw) : null;
    if (validPalette(value)) { memory.set(key, value); return value; }
  } catch { /* Unavailable storage must not block drawing. */ }
  return defaultPalette();
}

// Keep physical slots stable while touching a swatch. Only a new color replaces
// the least recently used slot; duplicate colors never consume another slot.
export function selectPaletteColor(palette, color) {
  if (typeof color !== 'string' || !validHex(color)) return palette;
  const value = color.toUpperCase();
  const colors = [...palette.colors];
  if (!colors.includes(value)) colors[colors.indexOf(palette.recent[palette.recent.length - 1])] = value;
  return { version: 1, colors, recent: [value, ...palette.recent.filter(previous => previous !== value && colors.includes(previous))] };
}

export function savePalette(userId, palette) {
  if (isDeletedOwner(userId)) return;
  const key = keyFor(userId);
  if (!key || !validPalette(palette)) return;
  // Retain colors for subsequent drawings in this session even if storage is
  // blocked/full. Preferences are separate from the disposable image draft.
  memory.set(key, palette);
  try { appLocalStorage.setItem(key, JSON.stringify(palette)); }
  catch { /* The in-memory palette remains available. */ }
}

export function forgetOwnerPalette(userId) { memory.delete(keyFor(userId)); }
